import { useCallback, useMemo, useState } from "react";
import { Pressable, View, Image } from "react-native";
import { Check } from "lucide-react-native";
import {
  getMedicationByBrandName,
  getSupplementByName,
} from "@src/packages/content";
import type { ContentBlock } from "@src/packages/content";
import MedicationFactsheetSheet from "../../../components/MedicationFactsheetSheet";
import type { MedicationUserContext } from "../../../components/MedicationFactsheetSheet";
import SupplementDetailSheet from "../../../components/SupplementDetailSheet";
import type { MedForm, MedicationScheduleItem } from "../schedule.types";
import { Row, StatusBadge, Tag } from "@louise/moon-ui";
import { semanticColors } from "@louise/moon-ui/theme/tokens";
import {
  isTriggerMedicationName,
  DOSE_STATUS_COLORS,
  DOSE_STATUS_ICONS,
  type DoseStatus,
} from "@src/packages/care/medications";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";

const BROWN = "#6B5548";

const pillCapsuleImage =
  require("../../../../assets/images/pill-capsule.png") as number;
const vialSyringeImage =
  require("../../../../assets/images/vial-syringe.png") as number;
const injectionPenImage =
  require("../../../../assets/images/injection-pen.png") as number;

export function MedFormIcon({
  form,
  size = 28,
}: {
  form: MedForm;
  size?: number;
}) {
  switch (form) {
    case "pen":
      return (
        <Image
          source={injectionPenImage}
          style={{ width: size, height: size }}
          resizeMode="contain"
        />
      );
    case "vial":
      return (
        <Image
          source={vialSyringeImage}
          style={{ width: size, height: size }}
          resizeMode="contain"
        />
      );
    case "pill":
    case "suppository":
      return (
        <Image
          source={pillCapsuleImage}
          style={{ width: size, height: size }}
          resizeMode="contain"
        />
      );
  }
}

type MedRowProps = {
  item: MedicationScheduleItem;
  /** Show checkbox for tracking completion */
  trackable?: boolean;
  done?: boolean;
  onToggle?: () => void;
  /** Subtitle override — e.g. "Daily · until Day 14" for recurring in Coming Up */
  subtitle?: string;
  /** Called when the user taps "Edit" inside the factsheet — opens the add/edit medication modal */
  onEditMedication?: (item: MedicationScheduleItem) => void;
  /** Status of the logged dose — drives checkbox color */
  doneStatus?: DoseStatus;
  /** Override dosage badges when one medication has multiple recorded doses */
  dosageBadges?: string[];
};

type ResolvedFactsheet =
  | { kind: "medication"; item: ContentBlock<"medication"> }
  | { kind: "supplement"; item: ContentBlock<"supplement"> }
  | { kind: "none" };

function resolveMedicationFactsheet(
  item: MedicationScheduleItem,
): ResolvedFactsheet {
  const name = item.brandName ?? item.name;
  const directMatch = getMedicationByBrandName(name);
  if (directMatch) return { kind: "medication", item: directMatch };

  const normalizedName = name.trim().toLowerCase();
  if (normalizedName === "progesterone") {
    const selectedForm = item.userContext?.selectedForm?.toLowerCase() ?? "";
    const route = item.userContext?.route?.toLowerCase() ?? "";
    if (route.includes("vaginal") || selectedForm.includes("suppository")) {
      const m = getMedicationByBrandName("Endometrin");
      if (m) return { kind: "medication", item: m };
    }
    if (route.includes("intramuscular") || selectedForm.includes("vial")) {
      const m = getMedicationByBrandName("Progesterone in Oil");
      if (m) return { kind: "medication", item: m };
    }
    const m = getMedicationByBrandName("Prometrium");
    if (m) return { kind: "medication", item: m };
  }

  // Fallback: user may have added a supplement as a "medication" — route to
  // the supplement detail sheet so they still get content instead of the
  // empty fallback.
  const supplementMatch = getSupplementByName(name);
  if (supplementMatch) {
    return { kind: "supplement", item: supplementMatch };
  }

  // No content match — still open the medication sheet in its minimal
  // "coming soon" mode so users can edit / stop / delete the medication.
  return { kind: "none" };
}

export default function MedRow({
  item,
  trackable = false,
  done = false,
  onToggle,
  subtitle,
  onEditMedication,
  doneStatus,
  dosageBadges,
}: MedRowProps) {
  const capturePress = usePressCapture("MedRow");
  const [openSheet, setOpenSheet] = useState<ResolvedFactsheet | null>(null);
  const [userCtx, setUserCtx] = useState<MedicationUserContext | undefined>(
    undefined,
  );
  const factsheetForRow = useMemo(
    () => resolveMedicationFactsheet(item),
    [item],
  );

  const handlePress = useCallback(() => {
    setOpenSheet(factsheetForRow);
    setUserCtx({
      ...item.userContext,
      originalName: item.brandName ?? item.name,
    });
  }, [factsheetForRow, item]);

  const closeSheet = useCallback(() => {
    setOpenSheet(null);
    setUserCtx(undefined);
  }, []);

  // A medication is "stopped" when the patient has explicitly halted it via
  // the Stop action — which prepends "Stopped" (or "Stopped: <reason>") to
  // notes. A regimen whose endDate naturally arrives is just "completed,"
  // not stopped, and shouldn't strike through.
  const isStopped = (() => {
    const notes = item.userContext?.notes;
    if (!notes) return false;
    return /^stopped(\s|:|$)/i.test(notes.trimStart());
  })();

  const defaultSubtitle = subtitle
    ? subtitle
    : item.sinceDay
      ? `Since ${item.sinceDay}`
      : undefined;
  const displayedDosages =
    dosageBadges && dosageBadges.length > 0
      ? dosageBadges
      : item.dosage
        ? [item.dosage]
        : [];
  const medicationIdPrefix = item.id.slice(0, 36);
  const medicationIdForSheet =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
      medicationIdPrefix,
    )
      ? medicationIdPrefix
      : undefined;
  const isTrigger = isTriggerMedicationName(item.name);
  const hasTitleRightContent =
    displayedDosages.length > 0 ||
    item.recentlyChanged ||
    isStopped ||
    isTrigger;

  const CheckIcon = doneStatus ? DOSE_STATUS_ICONS[doneStatus] : Check;
  const checkColor = doneStatus ? DOSE_STATUS_COLORS[doneStatus] : BROWN;

  const showStatusIndicator = !trackable && Boolean(doneStatus);
  const completionControl = trackable ? (
    <Pressable
      accessibilityRole="checkbox"
      accessibilityState={{ checked: done }}
      onPress={(event) => {
        event.stopPropagation?.();
        capturePress(pressLabel(item.name), "medication_dose_checkbox");
        onToggle?.();
      }}
      hitSlop={8}
      style={{
        width: 34,
        alignItems: "flex-end",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      <View
        style={{
          width: 24,
          height: 24,
          borderRadius: 12,
          backgroundColor: done ? checkColor : "transparent",
          borderWidth: done ? 0 : 1.5,
          borderColor: done ? checkColor : "rgba(0,0,0,0.12)",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {done ? <CheckIcon size={14} color="#fff" strokeWidth={2.5} /> : null}
      </View>
    </Pressable>
  ) : showStatusIndicator ? (
    <View
      accessibilityLabel={`Logged as ${doneStatus}`}
      style={{
        width: 34,
        alignItems: "flex-end",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      <View
        style={{
          width: 24,
          height: 24,
          borderRadius: 12,
          backgroundColor: checkColor,
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CheckIcon size={14} color="#fff" strokeWidth={2.5} />
      </View>
    </View>
  ) : null;

  return (
    <>
      <Row
        surface="plain"
        leadingMeta={item.time}
        leadingMetaWidth={50}
        leading={<MedFormIcon form={item.form} />}
        leadingSize={30}
        title={item.name}
        titleRight={
          hasTitleRightContent ? (
            <View
              style={{
                flexDirection: "row",
                flexWrap: "wrap",
                gap: 6,
                flexShrink: 1,
              }}
            >
              {isTrigger && <StatusBadge label="Trigger" tone="coral" />}
              {displayedDosages.map((dosage) => (
                <Tag key={dosage} label={dosage} tone="neutral" />
              ))}
              {item.recentlyChanged && (
                <StatusBadge label="Changed" tone="coral" />
              )}
              {isStopped && <StatusBadge label="Stopped" tone="coral" />}
            </View>
          ) : undefined
        }
        subtitle={defaultSubtitle || undefined}
        subtitleVariant="meta"
        onPress={handlePress}
        // Row captures for itself; the visible title is the medication.
        action="medication_row"
        muted={done}
        trailing={completionControl}
        chevron={!completionControl}
        paddingHorizontal={16}
        paddingVertical={12}
        titleStyle={{
          color: isStopped
            ? "rgba(42,37,34,0.48)"
            : semanticColors.typography.primary,
          textDecorationLine: isStopped ? "line-through" : "none",
        }}
        subtitleStyle={{
          color: isStopped
            ? "rgba(138,125,116,0.5)"
            : semanticColors.typography.supporting,
          textDecorationLine: isStopped ? "line-through" : "none",
        }}
      />

      {openSheet?.kind === "supplement" ? (
        <SupplementDetailSheet
          item={openSheet.item}
          onClose={closeSheet}
          userContext={userCtx}
        />
      ) : (
        <MedicationFactsheetSheet
          item={openSheet?.kind === "medication" ? openSheet.item : null}
          onClose={closeSheet}
          userContext={userCtx}
          medicationId={medicationIdForSheet}
          fallbackName={userCtx ? (item.brandName ?? item.name) : undefined}
          onEditPress={
            onEditMedication
              ? () => {
                  closeSheet();
                  onEditMedication(item);
                }
              : undefined
          }
        />
      )}
    </>
  );
}
